import type { AnalyticsAdapter, AnalyticsEvent } from '../types';
import { tracker } from '@/lib/tracking';

type TrackEventTargetType = 'post' | 'user' | 'channel' | 'tag' | 'vitals' | 'journey';

// Journey actions still mirrored client-side. Those persisted canonically by
// server-side handlers are intentionally OMITTED from this set so they don't
// double-write to user_interactions:
//   - sign_up         → app/api/auth/{google,x}/callback
//   - subscribe       → lib/payments/stripe.ts customer.subscription.created
//   - payment_approve → app/api/notifications/[id]/PATCH
//   - payment_deny    → app/api/notifications/[id]/PATCH
//   - first_post      → app/api/posts/POST (derived from EXISTS)
// These callsites still call useAnalytics().trackXxx() — InternalAdapter just
// drops the user_interactions write while GA4 / Google Ads adapters keep firing.
const JOURNEY_ACTIONS = new Set([
  'page_view', 'agent_search', 'agent_view',
]);

// Same dedup principle for engagement: post_like / post_comment / user_follow are
// canonically server-side via /api/posts/[id]/{vote,comments} and /api/users/[u]/follow.
// The frontend InternalAdapter no longer mirrors them. It DOES still mirror low-volume
// signal events (post_view, post_dwell) where the server has no chance to see them.
const ENGAGEMENT_ACTIONS: Record<string, { targetType: TrackEventTargetType; actionType: string }> = {
  post_view: { targetType: 'post', actionType: 'view' },
  post_click: { targetType: 'post', actionType: 'click' },
  post_repost: { targetType: 'post', actionType: 'repost' },
  post_share: { targetType: 'post', actionType: 'share' },
  post_follow: { targetType: 'post', actionType: 'follow_post' },
  post_dwell: { targetType: 'post', actionType: 'dwell' },
  user_view: { targetType: 'user', actionType: 'view' },
  channel_follow: { targetType: 'channel', actionType: 'follow' },
  channel_view: { targetType: 'channel', actionType: 'view' },
  tag_follow: { targetType: 'tag', actionType: 'follow' },
  tag_view: { targetType: 'tag', actionType: 'view' },
};

export class InternalAdapter implements AnalyticsAdapter {
  name = 'internal';
  requiredConsent = 'essential' as const;

  track(event: AnalyticsEvent) {
    if (!tracker) return;

    const props = event.properties ?? {};

    if (event.category === 'vitals' && event.name === 'web_vital') {
      tracker.trackWebVital(
        props.vitalName as string,
        props.vitalValue as number,
        props.vitalRating as string,
      );
      return;
    }

    if (event.category === 'journey' && JOURNEY_ACTIONS.has(event.name)) {
      switch (event.name) {
        case 'page_view':
          tracker.trackPageView(props.path as string, props.referrer as string | undefined);
          break;
        case 'sign_up':
          tracker.trackSignUp(props.method as string);
          break;
        case 'first_post':
          tracker.trackFirstPost();
          break;
        case 'agent_search':
          tracker.trackAgentSearch(props.query as string);
          break;
        case 'agent_view':
          tracker.trackAgentView(props.agentId as string);
          break;
        case 'subscribe':
          tracker.trackSubscribe(props.agentId as string);
          break;
        case 'payment_approve':
          tracker.trackPaymentApprove(props.agentId as string, props.amount as number);
          break;
        case 'payment_deny':
          tracker.trackPaymentDeny(props.agentId as string);
          break;
      }
      return;
    }

    const mapped = ENGAGEMENT_ACTIONS[event.name];
    if (mapped) {
      const targetId = (props.targetId ?? props.id ?? '') as string;
      if (mapped.actionType === 'dwell') {
        tracker.trackDwell(mapped.targetType, targetId, props.dwellTime as number);
      } else if (mapped.actionType === 'view') {
        tracker.trackView(mapped.targetType, targetId, props.source as string | undefined);
      } else if (mapped.actionType === 'follow') {
        tracker.trackFollow(mapped.targetType as 'user' | 'channel' | 'tag', targetId);
      } else {
        tracker.track({
          targetType: mapped.targetType,
          targetId,
          actionType: mapped.actionType as 'click',
          metadata: props.metadata as Record<string, unknown> | undefined,
        });
      }
      return;
    }

    // Splash / funnel UI events → persisted internally as journey rows
    // (target_type/action_type are plain text columns, so arbitrary ids/actions
    // are fine; metadata carries the detail).
    if (event.name === 'nav_click') {
      tracker.track({
        targetType: 'journey',
        targetId: String(props.target ?? 'cta'),
        actionType: 'nav_click',
        metadata: { source: props.source as string | undefined },
      });
      return;
    }
    if (event.name === 'content_action') {
      tracker.track({
        targetType: 'journey',
        targetId: String(props.contentId ?? ''),
        actionType: 'content_action',
        metadata: { action: props.action as string | undefined },
      });
      return;
    }
    if (event.name === 'cohort_assigned') {
      tracker.track({
        targetType: 'journey',
        targetId: String(props.cohort ?? 'default'),
        actionType: 'cohort_assigned',
        metadata: { source: props.source as string | undefined },
      });
      return;
    }

    // Generic fallback: navigation / engagement / commerce events not mapped above
    // are tracked as generic clicks to the internal tracker
    if (props.targetId) {
      tracker.trackClick((props.targetType ?? 'post') as TrackEventTargetType, props.targetId as string);
    }
  }

  page(path: string) {
    tracker?.trackPageView(path, typeof document !== 'undefined' ? document.referrer : undefined);
  }
}
